import { template } from "@ember/template-compiler";
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { readRecentNameSearches, rememberNameSearch, removeRecentNameSearch } from 'client-portal/utils/clinicianSearch/recent-name-searches';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import AttachTooltip from 'ember-attacher/components/attach-tooltip';
import Component from '@glimmer/component';
import RequestClinicianSearchFiltersPanel from '../filters/panel';
import styles from './name.module.scss';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import t from 'ember-intl/helpers/t';
export default class RequestClinicianSearchFacetFiltersName extends Component {
    @service
    intl;
    @service
    clinicianSearch;
    @service
    practiceStorage;
    @service
    media;
    styles = styles;
    @tracked
    nameDraft = '';
    @tracked
    recentNames = [];
    @tracked
    recentNameStatusByKey = {};
    constructor(){
        super(...arguments);
        this.nameDraft = this.clinicianSearch.query.name ?? '';
        this.recentNames = readRecentNameSearches(this.practiceStorage);
    }
    get filterOptionsLoadingAriaLabel() {
        return this.intl.t('booking.clinicianSearch.filterDetail.filterOptionsLoading');
    }
    get hasRecentNames() {
        return this.recentNames.length > 0;
    }
    mapRecentNameStatuses = (resolveStatus1)=>Object.fromEntries(this.recentNames.map((name1)=>[
                name1,
                resolveStatus1(name1)
            ]));
    refreshRecentNameStatuses = async ()=>{
        let routeParams1 = this.clinicianSearch.query.toRouteParams();
        try {
            this.recentNameStatusByKey = this.mapRecentNameStatuses((name1)=>this.clinicianSearch.sendAll.probeName(routeParams1, name1));
        } catch  {
            this.recentNameStatusByKey = this.mapRecentNameStatuses(()=>'no');
        }
    };
    recentNameRowDisabled = (name1)=>{
        return this.recentNameStatusByKey[name1] === 'no';
    };
    applyName = (rawName1)=>{
        let name1 = String(rawName1 ?? '').trim();
        if (name1.length) {
            rememberNameSearch(this.practiceStorage, name1);
        }
        this.recentNames = readRecentNameSearches(this.practiceStorage);
        this.clinicianSearch.query.applyFacetFilter('name', name1);
        this.clinicianSearch.mobile.backFromDetail();
    };
    apply = ()=>{
        this.applyName(this.nameDraft);
    };
    setNameDraft = (event1)=>{
        this.nameDraft = event1.target.value;
    };
    handleSearchKeydown = (event1)=>{
        if (event1.key === 'Enter') {
            event1.preventDefault();
            this.apply();
        }
    };
    removeRecent = (value1, event1)=>{
        event1?.preventDefault?.();
        event1?.stopPropagation?.();
        this.recentNames = removeRecentNameSearch(this.practiceStorage, value1);
    };
    removeRecentAriaLabel = (name1)=>this.intl.t('booking.clinicianSearch.filterDetail.removeRecentNameAria', {
            name: name1
        });
    recentDisabledHintId = (name1)=>{
        let key1 = String(name1 ?? '').trim().toLocaleLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
        return key1 ? `clinician-search-name-recent-disabled-${key1}` : null;
    };
    get recentDisabledHint() {
        return this.intl.t('booking.clinicianSearch.filterDetail.disabledFilterOptionName');
    }
    showRecentDisabledHint = (recent1)=>{
        return !this.media.isMdDown && this.recentNameRowDisabled(recent1);
    };
    static{
        template(`
    <RequestClinicianSearchFiltersPanel
      @class={{this.styles.component}}
      @facetKey="name"
      @isLoading={{this.clinicianSearch.sendAll.isLoading}}
      @loadingAriaLabel={{this.filterOptionsLoadingAriaLabel}}
      @onApply={{this.apply}}
      @onLoad={{this.refreshRecentNameStatuses}}
      data-test-clinician-search-filter-name-options
    >
      <div class="name-header">
        <p
          class="filter-section-title"
          id="clinician-search-name-filter-title"
          data-test-clinician-search-filter-name-panel-title
        >{{t "booking.clinicianSearch.filterDetail.nameFilterPanelTitle"}}</p>

        <div class="name-search-row">
          <span class="name-search-icon">{{svgJar
              "therapist-name-search"
              aria-hidden="true"
            }}</span>
          <input
            class="name-search-input"
            type="search"
            autocomplete="off"
            aria-label={{t "booking.clinicianSearch.filterDetail.nameSearchPlaceholder"}}
            placeholder={{t "booking.clinicianSearch.filterDetail.nameSearchPlaceholder"}}
            value={{this.nameDraft}}
            data-test-clinician-search-filter-name-input
            {{on "input" this.setNameDraft}}
            {{on "keydown" this.handleSearchKeydown}}
          />
        </div>
      </div>

      <div class="body">
        {{#if this.hasRecentNames}}
          <div
            class="name-recent-block"
            role="group"
            aria-labelledby="clinician-search-name-recent-heading"
          >
            <p class="name-recent-heading" id="clinician-search-name-recent-heading">{{t
                "booking.clinicianSearch.filterDetail.recentSearchHeading"
              }}</p>
            <ul class="name-recent-list" role="list">
              {{#each this.recentNames as |recent|}}
                <li class="name-recent-item">
                  <div class="name-recent-row">
                    <button
                      class="name-recent-button"
                      type="button"
                      disabled={{this.recentNameRowDisabled recent}}
                      aria-describedby={{if
                        (this.recentNameRowDisabled recent)
                        (this.recentDisabledHintId recent)
                      }}
                      data-test-clinician-search-filter-name-recent={{recent}}
                      {{on "click" (fn this.applyName recent)}}
                    >
                      {{recent}}
                    </button>
                    {{#if (this.showRecentDisabledHint recent)}}
                      <span id={{this.recentDisabledHintId recent}} class="visually-hidden">
                        {{this.recentDisabledHint}}
                      </span>
                      <AttachTooltip
                        @placement="right"
                        @class="tooltip align-left filter-disabled-tooltip-popover"
                        data-test-clinician-search-filter-name-recent-tooltip={{recent}}
                      >
                        {{this.recentDisabledHint}}
                      </AttachTooltip>
                    {{/if}}
                    <button
                      class="name-recent-remove-button"
                      type="button"
                      aria-label={{this.removeRecentAriaLabel recent}}
                      data-test-clinician-search-filter-name-recent-remove={{recent}}
                      {{on "click" (fn this.removeRecent recent)}}
                    >
                      {{svgJar "x-circ" aria-hidden="true"}}
                    </button>
                  </div>
                </li>
              {{/each}}
            </ul>
          </div>
        {{/if}}
      </div>
    </RequestClinicianSearchFiltersPanel>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
